import React, { useState, useRef } from "react";
import {
  X,
  Eye,
  Trash2,
  CloudDownload,
  CloudUpload,
  CheckCircle2,
  XCircle,
  Clock,
} from "lucide-react";
import ModalMod from "../../../../components/Model_Mod";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useDispatch, useSelector } from "react-redux";
import {
  AddSurplusDeficitData,
  fetchModDemandData,
  ModDemandData,
} from "../../../../Rest_api/RestApi";
import { ModLoadDataUpdate } from "../../../../store/state/ModLoadState";
import { ErrorPopup } from "../../../../components/ErrorHandling";

interface UpdateDataModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
  data?: any[];
  setData?: React.Dispatch<React.SetStateAction<any[]>>;
  modalTitle: string;
  preSelectedDate?: string;
  status?: string;
  onUploadSuccess?: (fileName: string) => void;
}

const UpdateSurplusDataModal: React.FC<UpdateDataModalProps> = ({
  data = [],
  setData,
  isModalOpen,
  setIsModalOpen,
  modalTitle,
  status,
  onUploadSuccess,
}) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const SelectedStartDate = useSelector(
    (state: any) => state.ProcurementOptimizerGridcoDate.startDate
  );
  const SelectedEndDate = useSelector(
    (state: any) => state.ProcurementOptimizerGridcoDate.endDate
  );

  const dispatch = useDispatch();

  const handleDownloadTemplate = () => {
    let exportData: any[] = [];
    const start = new Date(SelectedStartDate);
    const end = new Date(SelectedEndDate);

    for (
      let currentDate = new Date(start);
      currentDate <= end;
      currentDate.setDate(currentDate.getDate() + 1)
    ) {
      const formattedDate = currentDate.toISOString().split("T")[0];

      for (let i = 0; i < 96; i++) {
        exportData.push({
          date: formattedDate,
          time_slot: i + 1,
          deficit: "",
        });
      }
    }

    const headerMap: { [key: string]: string } = {
      date: "Date",
      time_slot: "Time Block",
      deficit: "Surplus (+)/Deficit (-)",
    };

    const keys = Object.keys(headerMap);

    const mappedData = exportData.map((row: Record<string, any>) =>
      keys.reduce((acc, key) => {
        acc[key] = row[key];
        return acc;
      }, {} as any)
    );

    const worksheet = XLSX.utils.json_to_sheet(mappedData, { header: keys });

    XLSX.utils.sheet_add_aoa(worksheet, [keys.map((k) => headerMap[k])], {
      origin: "A1",
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `${modalTitle}.xlsx`);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const handleViewFile = () => {
    if (uploadedFile) {
      const url = URL.createObjectURL(uploadedFile);
      window.open(url, "_blank");
      URL.revokeObjectURL(url);
    }
  };

  const handleDeleteFile = () => {
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = () => {
    if (!uploadedFile) {
      setError("Please upload a file first.");
      return;
    }

    const reader = new FileReader();

    reader.onload = async (e) => {
      setLoading(true);
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });

        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        const headerReverseMap: { [key: string]: string } = {
          Date: "date",
          "Time Block": "time_slot",
          "Surplus (+)/Deficit (-)": "deficit",
        };

        const firstRow = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
        })[0] as string[];

        const missingHeaders = Object.keys(headerReverseMap).filter(
          (header) => !firstRow.includes(header)
        );

        if (missingHeaders.length > 0) {
          setError(
            `Invalid file format. Missing columns: ${missingHeaders.join(", ")}`
          );
          setLoading(false);
          return;
        }

        const formattedData = (jsonData as any[]).map((row) => {
          const newRow: any = {};

          Object.entries(headerReverseMap).forEach(
            ([excelHeader, internalKey]) => {
              newRow[internalKey] = row[excelHeader] ?? "";
            }
          );

          return newRow;
        });
        await AddSurplusDeficitData(formattedData);
        // Call success callback
        if (onUploadSuccess && uploadedFile) {
          onUploadSuccess(uploadedFile.name);
        }

        setIsModalOpen(false);
        setUploadedFile(null);
        setLoading(false);
      } catch (error) {
        console.error("Error processing file:", error);
        setError("There was an error processing the file. Please try again.");
        setLoading(false);
      }
    };

    reader.readAsArrayBuffer(uploadedFile);
  };

  return (
    <>
      <ModalMod
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setUploadedFile(null);
        }}
        width="550px"
        showCloseButton={false}
      >
        <div
          className="p-3"
          style={{
            backgroundColor: "#FFFFFF",
            borderBottom: "1px solid #DFEAF2",
          }}
        >
          <div className="flex items-center justify-between">
            <h2
              className="text-lg font-medium"
              style={{
                color: "#525062",
                fontFamily: "Marsden Wide",
                fontWeight: 500,
                fontSize: "18px",
                lineHeight: "100%",
                letterSpacing: "0px",
              }}
            >
              Upload
            </h2>
            <button
              onClick={() => {
                setIsModalOpen(false);
                setUploadedFile(null);
              }}
              className="hover:bg-gray-200 p-1 rounded transition-colors"
              style={{ color: "#777777" }}
              aria-label="Close modal"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="p-2 space-y-2">
          <div
            className="p-2"
            style={{ backgroundColor: "#FFFFFF", borderRadius: "8px" }}
          >
            <h3
              className="text-sm font-medium mb-2"
              style={{ color: "#6F6D7C" }}
            >
              Instruction to Create New Session
            </h3>
            <ul
              className="list-disc pl-5 space-y-1 text-sm"
              style={{ color: "#8C8A96" }}
            >
              <li>Share the surplus–deficit profile</li>
              <li>Add the available trader offers</li>
              <li>Choose the DAM market rates from available scenarios</li>
            </ul>
          </div>

          <div className="space-y-4 border border-[#DFEAF2] p-4 rounded-xl">
            <div className="flex items-center justify-between">
              <span
                className="text-sm font-medium"
                style={{ color: "#6F6D7C" }}
              >
                Surplus Deficit Profile
              </span>
              <button
                onClick={handleDownloadTemplate}
                className="flex items-center justify-center gap-2 w-[130px] h-[39px] text-sm rounded-lg border-[0.5px] border-[#F1935C] font-medium transition-colors"
                style={{ backgroundColor: "#FFFAF6", color: "#F1935C" }}
                aria-label="Download template"
              >
                Download <CloudDownload strokeWidth={3} size={18} />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <span
                className="text-sm font-medium"
                style={{ color: "#6C757D" }}
              >
                Upload Populated File
              </span>
              {uploadedFile ? (
                <div className="flex items-center gap-3">
                  <span
                    className="text-sm truncate block max-w-[200px]"
                    style={{ color: "#525062" }}
                  >
                    {uploadedFile.name}
                  </span>
                  <button
                    onClick={handleViewFile}
                    className="text-orange-500 transition-colors"
                    aria-label="View file"
                  >
                    <Eye size={20} />
                  </button>
                  <button
                    onClick={handleDeleteFile}
                    className="text-[#6F6D7C] hover:text-red-500 transition-colors"
                    aria-label="Delete file"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleUploadClick}
                  className="flex items-center justify-center gap-2 w-[130px] h-[39px] text-sm rounded-lg border-[0.5px] border-[#34656D] font-medium transition-colors"
                  style={{ backgroundColor: "#EFFCFF", color: "#34656D" }}
                  aria-label="Upload file"
                >
                  Upload <CloudUpload strokeWidth={3} size={18} />
                </button>
              )}
              <input
                type="file"
                accept=".xlsx"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
                aria-label="File input"
              />
            </div>
          </div>
        </div>

        <div
          className="px-4 py-3 flex justify-center"
          style={{
            backgroundColor: "#FFFFFF",
            borderTop: "1px solid #E1E5E9",
          }}
        >
          <button
            type="submit"
            disabled={loading}
            onClick={handleSubmit}
            className="flex items-center justify-center px-12 py-2.5 bg-[#F1935C] text-white text-xl rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {loading ? "Processing..." : "Submit"}
          </button>
        </div>
      </ModalMod>
      <ErrorPopup error={error} onClose={() => setError(null)} />
    </>
  );
};

// Upload status type
export type UploadStatus = "pending" | "submitted" | "error";

interface UploadBtnProps {
  data?: any[];
  setData?: React.Dispatch<React.SetStateAction<any[]>>;
  modalTitle?: string;
  preSelectedDate?: string;
  uploadStatus?: UploadStatus;
  uploadedFileName?: string;
  onUploadSuccess?: (fileName: string) => void;
}

export default function UploadBtn({
  data,
  setData,
  modalTitle = "Upload template",
  preSelectedDate,
  uploadStatus = "pending",
  uploadedFileName,
  onUploadSuccess,
}: UploadBtnProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getStatusDisplay = () => {
    switch (uploadStatus) {
      case "submitted":
        return {
          icon: <CheckCircle2 size={20} className="text-green-500" />,
          text: "Submitted",
          textColor: "#10B981",
          bgColor: "#ECFDF5",
        };
      case "error":
        return {
          icon: <XCircle size={20} className="text-red-500" />,
          text: "Error",
          textColor: "#EF4444",
          bgColor: "#FEF2F2",
        };
      case "pending":
      default:
        return {
          icon: "",
          text: "",
          textColor: "#9CA3AF",
          bgColor: "#F9FAFB",
        };
    }
  };

  const statusDisplay = getStatusDisplay();

  return (
    <div className="bg-[#FFFFFF] rounded-2xl border-[2px] border-[#CAD5E2] px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-2">
          <span className="text-[#6F6D7C] text-lg font-medium">
           System Balance Profile (Surplus/Deficit)
          </span>
        </div>

        <div className="flex items-center gap-4 justify-end">
          <div className="flex items-center gap-3">
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
              style={{
                backgroundColor: statusDisplay.bgColor,
              }}
            >
              {statusDisplay.icon}
              <span
                className="text-sm font-medium"
                style={{ color: statusDisplay.textColor }}
              >
                {statusDisplay.text}
              </span>
            </div>

            {uploadedFileName && uploadStatus === "submitted" && (
              <span className="text-sm text-[#6F6D7C] truncate max-w-[200px]">
                {uploadedFileName}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="flex items-center justify-center px-4 py-2 gap-2 text-xl rounded-xl border border-[#34656D] font-medium transition-colors hover:opacity-90"
            style={{ backgroundColor: "#EFFCFF", color: "#34656D" }}
          >
            <CloudUpload strokeWidth={3} size={20} /> Upload
          </button>
          <UpdateSurplusDataModal
            data={data}
            setData={setData}
            setIsModalOpen={setIsModalOpen}
            isModalOpen={isModalOpen}
            modalTitle={modalTitle}
            preSelectedDate={preSelectedDate}
            onUploadSuccess={onUploadSuccess}
          />
        </div>
      </div>
    </div>
  );
}
